import { z } from 'zod';

import { type ValidationMessageSet } from '../../form-validations';
import { getCaptchaTokenSchema } from '../../schemas/captcha-token-schema';
import { getEmailSchema } from '../../schemas/email-schema';
import {
  type NameValidationMessages,
  getNameSchema,
} from '../../schemas/name-schema';
import { getPhoneSchema } from '../../schemas/phone-schema';

type Messages = {
  campusRequiredMessage: string;
  courseRequiredMessage: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  firstNameValidationMessages: NameValidationMessages;
  inquiryRequiredMessage: string;
  lastNameValidationMessages: NameValidationMessages;
  phoneInvalidMessage: string;
  phoneRequiredMessage: string;
};

// Validation shared between client form & API payload
// Messages aren't needed for API payload validation
export const getBaseSchema = (messages?: Messages) =>
  z
    .object({
      message: z.string().trim().min(2, messages?.inquiryRequiredMessage),
    })
    .merge(getEmailSchema(messages?.emailValidationMessages));

// Validation for client form only
export const getContactSchema = (messages: Messages) =>
  z
    .object({
      newsletter: z.boolean(),
      phone: getPhoneSchema(messages),
      selection: z
        .object({
          campus: z.string(),
          courseType: z.enum(['in_person', 'online']),
          track: z.string().min(2, messages.courseRequiredMessage),
        })
        .superRefine((sel, { addIssue }) => {
          if (sel.courseType === 'in_person' && sel.campus === '') {
            addIssue({
              code: z.ZodIssueCode.custom,
              path: ['campus'],
              message: messages.campusRequiredMessage,
            });
          }
        }),
    })
    .merge(getBaseSchema(messages))
    .merge(getCaptchaTokenSchema({ requiredMessage: messages.errorMessage }))
    .merge(
      getNameSchema(
        messages.firstNameValidationMessages,
        messages.lastNameValidationMessages
      )
    )
    .required();
